import { useState, useEffect, useCallback, useMemo } from 'react';
import { CHANGELOG_LATEST_DATE } from '../data/changelogLatest';
import { safeGetItem, safeSetItem } from '../utils/storage';

const storageKey = (userId) => `kaiylo_changelog_seen_${userId}`;

// 'AAAA-MM-JJ' se compare correctement en tant que chaîne.
const sortByDateDesc = (entries) => [...entries].sort((a, b) => b.date.localeCompare(a.date));

/**
 * Pop-up « Nouveautés » due ? Oui si la mise à jour mise en avant
 * (`spotlightDate`) n'a pas encore été vue sur cet appareil et que le coach
 * était inscrit avant elle (`createdAt` inconnu : on la montre).
 */
export const isWhatsNewDue = (seenDate, createdAt, spotlightDate) => {
  if (!spotlightDate || (seenDate || '') >= spotlightDate) return false;
  const joined = typeof createdAt === 'string' ? createdAt.slice(0, 10) : null;
  return !joined || joined < spotlightDate;
};

/** Une entrée est « non vue » si sa date est postérieure à la dernière ouverture du volet. */
export const isChangelogEntryUnseen = (entry, lastSeenDate) =>
  !lastSeenDate || entry.date > lastSeenDate;

/**
 * Nouveautés coach + suivi du « déjà vu », par utilisateur et par appareil
 * (localStorage). `lastSeenDate` est la date de l'entrée la plus récente au
 * moment où le coach a ouvert le volet pour la dernière fois.
 *
 * Les textes (data/changelog.js, deux langues) sont chargés à part, après
 * l'affichage : l'en-tête qui utilise ce hook est dans le bundle principal,
 * déjà au plafond de taille. La pastille n'a besoin que de la date de la
 * dernière nouveauté (data/changelogLatest.js). `entries` passé = tests.
 */
const useChangelog = (userId, entries = null) => {
  const [loadedEntries, setLoadedEntries] = useState(null);

  useEffect(() => {
    if (entries || !userId || loadedEntries) return undefined;
    let cancelled = false;
    import('../data/changelog')
      .then((m) => { if (!cancelled) setLoadedEntries(m.CHANGELOG); })
      .catch(() => null);
    return () => { cancelled = true; };
  }, [entries, userId, loadedEntries]);

  const source = entries || loadedEntries;
  const sorted = useMemo(() => (source ? sortByDateDesc(source) : []), [source]);
  const latestDate = entries ? (sorted[0]?.date ?? null) : CHANGELOG_LATEST_DATE;

  const [lastSeenDate, setLastSeenDate] = useState(() => (userId ? safeGetItem(storageKey(userId)) : null));

  useEffect(() => {
    setLastSeenDate(userId ? safeGetItem(storageKey(userId)) : null);
  }, [userId]);

  const hasUnseen = Boolean(userId && latestDate && (!lastSeenDate || latestDate > lastSeenDate));

  const markAllSeen = useCallback(() => {
    if (!userId || !latestDate) return;
    safeSetItem(storageKey(userId), latestDate);
    setLastSeenDate(latestDate);
  }, [userId, latestDate]);

  return { entries: sorted, lastSeenDate, hasUnseen, markAllSeen };
};

export default useChangelog;
